import React, { useState, useEffect } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function GroupsTabModern({
  groups,
  children,
  authHeaders,
  toast,
  fetchGroups,
  isSuperAdmin,
}) {
  const [editingGroup, setEditingGroup] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [presetColors, setPresetColors] = useState([]);
  const [form, setForm] = useState({
    name: "",
    trainer: "",
    weekday: "Sa.",
    start_time: "",
    end_time: "",
    color: "#3b82f6",
    order: "",
    changing_room_boys: "",
    changing_room_girls: "",
  });

  // Farben vom Backend laden
  useEffect(() => {
    loadPresetColors();
  }, []);

  const loadPresetColors = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/group-color-presets`);
      setPresetColors(res.data.colors || []);
    } catch (e) {
      console.error("Fehler beim Laden der Gruppenfarben:", e);
      // Fallback auf Standard-Farben
      setPresetColors([
        { hex: "#ef4444", name: "Rot" },
        { hex: "#f97316", name: "Orange" },
        { hex: "#eab308", name: "Gelb" },
        { hex: "#22c55e", name: "Grün" },
        { hex: "#14b8a6", name: "Türkis" },
        { hex: "#3b82f6", name: "Blau" },
        { hex: "#8b5cf6", name: "Violett" },
        { hex: "#ec4899", name: "Pink" },
        { hex: "#6b7280", name: "Grau" },
      ]);
    }
  };

  // Kinder pro Gruppe zählen
  const getChildCount = (groupId) => {
    return (children || []).filter((c) => c.group_id === groupId && c.status !== "archiviert").length;
  };

  // Formular zurücksetzen
  const resetForm = () => {
    setForm({
      name: "",
      trainer: "",
      weekday: "Sa.",
      start_time: "",
      end_time: "",
      color: "#3b82f6",
      order: "",
      changing_room_boys: "",
      changing_room_girls: "",
    });
    setEditingGroup(null);
    setShowForm(false);
  };

  // Gruppe bearbeiten
  const startEdit = (group) => {
    setForm({
      name: group.name || "",
      trainer: group.trainer || "",
      weekday: group.weekday || "Sa.",
      start_time: group.start_time || "",
      end_time: group.end_time || "",
      color: group.color || "#3b82f6",
      order: group.order || "",
      changing_room_boys: group.changing_room_boys || "",
      changing_room_girls: group.changing_room_girls || "",
    });
    setEditingGroup(group);
    setShowForm(true);
  };

  // Gruppe speichern
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.start_time || !form.end_time) {
      toast({ title: "Bitte alle Pflichtfelder ausfüllen", variant: "destructive" });
      return;
    }

    try {
      const payload = {
        name: form.name,
        trainer: form.trainer,
        weekday: form.weekday,
        start_time: form.start_time,
        end_time: form.end_time,
        color: form.color,
        order: form.order ? parseInt(form.order) : null,
        changing_room_boys: form.changing_room_boys || null,
        changing_room_girls: form.changing_room_girls || null,
      };

      if (editingGroup) {
        await axios.put(`${API_BASE}/api/groups/${editingGroup.id}`, payload, authHeaders());
        toast({ title: "Gruppe aktualisiert" });
      } else {
        await axios.post(`${API_BASE}/api/groups`, payload, authHeaders());
        toast({ title: "Gruppe erstellt" });
      }
      
      fetchGroups();
      resetForm();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Fehler beim Speichern der Gruppe";
      toast({ title: msg, variant: "destructive" });
    }
  };

  // Gruppe löschen
  const handleDelete = async (group) => {
    try {
      await axios.delete(`${API_BASE}/api/groups/${group.id}`, authHeaders());
      toast({ title: "Gruppe gelöscht" });
      fetchGroups();
      setDeleteConfirm(null);
      if (editingGroup?.id === group.id) {
        resetForm();
      }
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Fehler beim Löschen der Gruppe";
      toast({ title: msg, variant: "destructive" });
    }
  };

  // Gruppen nach order sortieren
  const sortedGroups = [...(groups || [])].sort((a, b) => (a.order || 999) - (b.order || 999));

  // Gruppe nach oben/unten verschieben
  const handleMoveGroup = async (group, direction) => {
    const currentIndex = sortedGroups.findIndex(g => g.id === group.id);
    const newIndex = currentIndex + direction;
    
    if (newIndex < 0 || newIndex >= sortedGroups.length) return;
    
    const otherGroup = sortedGroups[newIndex];
    
    try {
      // Tausche die order-Werte
      const currentOrder = group.order || currentIndex + 1;
      const otherOrder = otherGroup.order || newIndex + 1;
      
      await Promise.all([
        axios.put(`${API_BASE}/api/groups/${group.id}`, { order: otherOrder }, authHeaders()),
        axios.put(`${API_BASE}/api/groups/${otherGroup.id}`, { order: currentOrder }, authHeaders())
      ]);
      
      fetchGroups();
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Verschieben", variant: "destructive" });
    }
  };

  // Textfarbe basierend auf Hintergrund
  const getTextColor = (bgColor) => {
    if (!bgColor || !bgColor.startsWith("#")) return "#1f2937";
    try {
      const r = parseInt(bgColor.slice(1, 3), 16);
      const g = parseInt(bgColor.slice(3, 5), 16);
      const b = parseInt(bgColor.slice(5, 7), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#1f2937" : "#ffffff";
    } catch {
      return "#1f2937";
    }
  };

  // Nicht-Super-Admin Ansicht
  if (!isSuperAdmin) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl bg-amber-50 p-4 text-center">
          <p className="text-sm text-amber-700">
            Nur Super-Administratoren können Gruppen verwalten.
          </p>
        </div>
        
        {/* Gruppen nur anzeigen */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((group) => (
            <div
              key={group.id}
              className="rounded-xl bg-white p-4 shadow-sm"
              style={{ borderLeft: `4px solid ${group.color || "#3b82f6"}` }}
            >
              <div className="font-medium text-slate-900">{group.name}</div>
              <div className="mt-1 text-sm text-slate-500">
                {group.trainer} · {group.weekday} {group.start_time?.slice(0, 5)} - {group.end_time?.slice(0, 5)}
              </div>
              <div className="mt-2 text-xs text-slate-400">
                {getChildCount(group.id)} Kinder
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="groups-tab-modern">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-white p-4 shadow-sm">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Schwimmgruppen</h2>
          <p className="text-sm text-slate-500" data-testid="groups-count">{groups.length} Gruppen verwalten</p>
        </div>
        <button
          data-testid="new-group-button"
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
          }}
          className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
            showForm && !editingGroup
              ? "bg-slate-900 text-white"
              : "bg-violet-100 text-violet-700 hover:bg-violet-200"
          }`}
        >
          {showForm && !editingGroup ? "Abbrechen" : "+ Neue Gruppe"}
        </button>
      </div>

      {/* Lösch-Bestätigung */}
      {deleteConfirm && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-100">
              <svg className="h-5 w-5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-rose-900">Gruppe löschen?</h4>
              <p className="mt-1 text-xs text-rose-700">
                Möchten Sie die Gruppe <strong>"{deleteConfirm.name}"</strong> wirklich löschen?
                {getChildCount(deleteConfirm.id) > 0 && (
                  <span className="block mt-1 font-medium">
                    ⚠️ Diese Gruppe hat noch {getChildCount(deleteConfirm.id)} Kinder zugeordnet!
                  </span>
                )}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="rounded-full bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-500"
                >
                  Ja, löschen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formular */}
      {showForm && (
        <div className="rounded-xl bg-gradient-to-br from-violet-50 to-white p-4 shadow-sm">
          <h3 className="mb-4 text-sm font-semibold text-violet-900">
            {editingGroup ? `"${editingGroup.name}" bearbeiten` : "Neue Gruppe anlegen"}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="group-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-slate-500">Gruppenname *</label>
                <input
                  type="text"
                  data-testid="group-name-input"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="z.B. Gruppe 1"
                  required
                  className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Trainer</label>
                <input
                  type="text"
                  data-testid="group-trainer-input"
                  value={form.trainer}
                  onChange={(e) => setForm((f) => ({ ...f, trainer: e.target.value }))}
                  placeholder="z.B. Max Mustermann"
                  className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-4">
              <div>
                <label className="mb-1 block text-xs text-slate-500">Wochentag</label>
                <select
                  data-testid="group-weekday-select"
                  value={form.weekday}
                  onChange={(e) => setForm((f) => ({ ...f, weekday: e.target.value }))}
                  className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="Mo.">Montag</option>
                  <option value="Di.">Dienstag</option>
                  <option value="Mi.">Mittwoch</option>
                  <option value="Do.">Donnerstag</option>
                  <option value="Fr.">Freitag</option>
                  <option value="Sa.">Samstag</option>
                  <option value="So.">Sonntag</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Von *</label>
                <input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                  required
                  className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Bis *</label>
                <input
                  type="time"
                  value={form.end_time}
                  onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                  required
                  className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Reihenfolge</label>
                <input
                  type="number"
                  min="1"
                  value={form.order}
                  onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                  placeholder="1"
                  className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            </div>

            {/* Farbauswahl */}
            <div>
              <label className="mb-2 block text-xs text-slate-500">Gruppenfarbe</label>
              <div className="flex flex-wrap items-center gap-2">
                {presetColors.map((colorObj, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, color: colorObj.hex }))}
                    title={colorObj.name || colorObj.hex}
                    className={`h-8 w-8 rounded-lg transition-transform hover:scale-110 ${
                      form.color === colorObj.hex ? "ring-2 ring-offset-2 ring-slate-400" : ""
                    }`}
                    style={{ backgroundColor: colorObj.hex }}
                  />
                ))}
              </div>
              {/* HEX-Eingabe */}
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  className="h-8 w-10 cursor-pointer rounded border-0"
                />
                <input
                  type="text"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  placeholder="#3b82f6"
                  className="w-28 rounded-lg border border-slate-200 px-2 py-1.5 text-sm font-mono focus:border-slate-400 focus:outline-none"
                />
                <span className="text-xs text-slate-400">HEX-Code</span>
              </div>
            </div>

            {/* Umkleidekabinen */}
            <div className="rounded-lg bg-blue-50/50 p-4">
              <label className="mb-3 flex items-center gap-2 text-xs font-semibold text-blue-900">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                </svg>
                Umkleidekabinen
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-blue-500 text-[10px] font-bold text-white">J</span>
                    Jungen
                  </label>
                  <input
                    type="text"
                    data-testid="group-changing-room-boys"
                    value={form.changing_room_boys}
                    onChange={(e) => setForm((f) => ({ ...f, changing_room_boys: e.target.value }))}
                    placeholder="z.B. Kabine 1 oder 2, Nemo"
                    className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="mb-1 flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-pink-500 text-[10px] font-bold text-white">M</span>
                    Mädchen
                  </label>
                  <input
                    type="text"
                    data-testid="group-changing-room-girls"
                    value={form.changing_room_girls}
                    onChange={(e) => setForm((f) => ({ ...f, changing_room_girls: e.target.value }))}
                    placeholder="z.B. Kabine 4 oder 5, Ariel"
                    className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>
              </div>
            </div>

            {/* Vorschau */}
            <div className="rounded-lg bg-white p-3 shadow-inner">
              <div className="text-xs text-slate-400 mb-2">Vorschau:</div>
              <div
                className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium"
                style={{ backgroundColor: form.color, color: getTextColor(form.color) }}
              >
                {form.name || "Gruppenname"} · {form.start_time?.slice(0, 5) || "00:00"}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="group-cancel-button"
                onClick={resetForm}
                className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="group-submit-button"
                className="rounded-full bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500"
              >
                {editingGroup ? "Speichern" : "Gruppe erstellen"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Gruppen-Liste */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="groups-grid">
        {sortedGroups.length === 0 ? (
          <div className="col-span-full rounded-xl bg-white p-8 text-center shadow-sm">
            <div className="mb-2 text-4xl">👥</div>
            <p className="text-sm text-slate-500">Noch keine Gruppen angelegt.</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-3 rounded-full bg-violet-100 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-200"
            >
              Erste Gruppe erstellen
            </button>
          </div>
        ) : (
          sortedGroups.map((group, index) => {
            const childCount = getChildCount(group.id);
            const textColor = getTextColor(group.color);
            
            return (
              <div
                key={group.id}
                data-testid={`group-card-${group.id}`}
                className={`rounded-xl bg-white shadow-sm overflow-hidden transition-all ${
                  editingGroup?.id === group.id ? "ring-2 ring-violet-400" : ""
                }`}
              >
                {/* Farbiger Header */}
                <div
                  className="px-4 py-3"
                  style={{ backgroundColor: group.color || "#3b82f6" }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {/* Reihenfolge-Buttons */}
                      <div className="flex flex-col gap-0.5">
                        <button
                          onClick={() => handleMoveGroup(group, -1)}
                          disabled={index === 0}
                          className="rounded p-0.5 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Nach oben"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke={textColor} strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleMoveGroup(group, 1)}
                          disabled={index === sortedGroups.length - 1}
                          className="rounded p-0.5 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Nach unten"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke={textColor} strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                      <span className="font-semibold" style={{ color: textColor }}>
                        {group.name}
                      </span>
                    </div>
                    <span
                      className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium"
                      style={{ color: textColor }}
                    >
                      {childCount} Kinder
                    </span>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {group.trainer || "–"}
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {group.weekday} {group.start_time?.slice(0, 5)} – {group.end_time?.slice(0, 5)}
                    </div>
                  </div>
                  
                  {/* Aktionen */}
                  <div className="mt-4 flex gap-2">
                    <button
                      data-testid={`edit-group-${group.id}`}
                      onClick={() => startEdit(group)}
                      className="flex-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200"
                    >
                      Bearbeiten
                    </button>
                    <button
                      data-testid={`delete-group-${group.id}`}
                      onClick={() => setDeleteConfirm(group)}
                      className="rounded-lg px-3 py-1.5 text-xs text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
